import axiosInstance from "./axios";
import { ResponseType } from "@/types/CommonTypes";
import {
  ClientLimits,
  ClientLimitType,
  ClientLimitsResponse,
  ClientLimitsStatusResponse,
  MyLimitsData,
} from "@/types/LimitsTypes";

export type { ClientLimitKey, ClientLimits, ClientLimitType, ClientLimitsResponse, ClientLimitStatusValue, ClientLimitsStatusResponse, MyLimitsEntityData, MyLimitsCombined, MyLimitsData } from "@/types/LimitsTypes";

export const limitsApi = {
  getClientLimits: async (
    clientId: number,
  ): Promise<ResponseType<ClientLimitsResponse>> => {
    try {
      const response = await axiosInstance.get(`/limits/client/${clientId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getLimitTypes: async (): Promise<ResponseType<ClientLimitType[]>> => {
    try {
      const response = await axiosInstance.get("/limits/types");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateClientLimits: async (
    clientId: number,
    payload: Partial<ClientLimits>,
  ): Promise<ResponseType<ClientLimitsResponse>> => {
    try {
      const response = await axiosInstance.patch(
        `/limits/client/${clientId}`,
        payload,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getClientLimitsStatus: async (
    clientId: number,
    signal?: AbortSignal,
  ): Promise<ResponseType<ClientLimitsStatusResponse>> => {
    try {
      const response = await axiosInstance.get(
        `/limits/client/${clientId}/status`,
        { signal },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getMyLimits: async (): Promise<ResponseType<MyLimitsData>> => {
    try {
      const response = await axiosInstance.get("/limits/me");
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

/** The coaching modes a limit can be set on. */
export type LimitEntityType = "client" | "group" | "user";

export type CoachLimitPeriod = "total" | "monthly";

export interface CoachLimitValue {
  limit: number | null;
  used: number | null;
  period: CoachLimitPeriod | null;
  /** Which level set the limit that binds — client, group or user. */
  level?: LimitEntityType | null;
  entity_id?: number;
  exceeded: boolean;
  unit?: string;
  label?: string;
}

export interface CoachLimitType {
  key: string;
  label: string;
  unit: string;
  defaultPeriod: CoachLimitPeriod;
}

export interface CoachLimitsResponse {
  limits: Record<string, CoachLimitValue>;
  exceeded?: string[];
  types?: CoachLimitType[];
}

export const coachLimitsApi = {
  /** The signed-in user's own coaching budget. Needs no permission. */
  getMine: async (): Promise<ResponseType<CoachLimitsResponse>> => {
    try {
      const response = await axiosInstance.get("/limits/coach/me");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getForEntity: async (
    entityType: LimitEntityType,
    entityId: number,
  ): Promise<ResponseType<CoachLimitsResponse>> => {
    try {
      const response = await axiosInstance.get(`/limits/coach/${entityType}/${entityId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  setForEntity: async (
    entityType: LimitEntityType,
    entityId: number,
    values: Record<string, { limit: number | null; period?: CoachLimitPeriod }>,
  ): Promise<ResponseType<CoachLimitsResponse>> => {
    try {
      const response = await axiosInstance.put(`/limits/coach/${entityType}/${entityId}`, values);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
